import {PropsExport} from "@AppBuilderLib/entities/export/config/propsExport";
import {PropsOutput} from "@AppBuilderLib/entities/output/config/propsOutput";
import {PropsParameter} from "@AppBuilderLib/entities/parameter/config/propsParameter";
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {useShapeDiverStoreStargate} from "@AppBuilderLib/entities/stargate/model/useShapeDiverStoreStargate";
import DesktopClientPanel from "@AppBuilderLib/entities/stargate/ui/DesktopClientPanel";
import {
	AttributeVisualizationVisibility,
	IAppBuilderSettingsSession,
} from "@AppBuilderLib/features/appbuilder/config/appbuilder";
import {ComponentContext} from "@AppBuilderLib/features/appbuilder/config/ComponentContext";
import TabsComponent, {
	ITabsComponentProps,
} from "@AppBuilderLib/shared/ui/tabs/TabsComponent";
import AppBuilderSavedStatesWidgetComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderSavedStatesWidgetComponent";
import {useContext, useMemo} from "react";
import {useShallow} from "zustand/react/shallow";
import ParametersAndExportsAccordionComponent from "./ParametersAndExportsAccordionComponent";

interface Props {
	parameters: PropsParameter[];
	exports: PropsExport[];
	outputs: PropsOutput[];
	namespace?: string;
	settings?: IAppBuilderSettingsSession;
}

export default function AppBuilderFallbackContainerComponent({
	parameters,
	exports,
	outputs,
	namespace,
	settings,
}: Props) {
	const showDesktopClientPanel = useShapeDiverStoreStargate(
		useShallow((state) => state.referenceCount > 0),
	);
	const {sessionApi} = useShapeDiverStoreSession(
		useShallow((state) => ({
			sessionApi: namespace ? state.sessions[namespace] : undefined,
		})),
	);

	const componentContext = useContext(ComponentContext);

	// get the attribute visualization widget component from the component context
	// if one is registered
	const AppBuilderAttributeVisualizationWidgetComponent =
		componentContext.widgets?.attributeVisualization.component;

	const tabProps: ITabsComponentProps = useMemo(() => {
		const tabProps: ITabsComponentProps = {
			defaultValue: "",
			tabs: [],
		};
		const hideExportsTab =
			settings?.hideExports ||
			(exports.length == 0 && outputs.length == 0);

		if (parameters.length > 0) {
			const props: {
				parameters: PropsParameter[];
				exports?: PropsExport[];
				outputs?: PropsOutput[];
			} = {
				parameters,
			};
			if (hideExportsTab) {
				props.exports = exports;
				props.outputs = outputs;
			}

			tabProps.defaultValue = "Parameters";
			tabProps.tabs.push({
				value: "Parameters",
				tooltip: "Parameters",
				icon: "tabler:adjustments-horizontal",
				children: [
					<ParametersAndExportsAccordionComponent
						key={0}
						{...props}
					/>,
				],
			});
		}
		if (!hideExportsTab) {
			tabProps.defaultValue = tabProps.defaultValue || "Exports";
			tabProps.tabs.push({
				value: "Exports",
				tooltip: "Exports",
				icon: "tabler:download",
				children: [
					<ParametersAndExportsAccordionComponent
						key={1}
						exports={exports}
						outputs={outputs}
						namespace={namespace}
					/>,
				],
			});
		}

		if (!settings?.hideSavedStates) {
			tabProps.tabs.push({
				value: "Saved States",
				tooltip: "Saved States",
				icon: "tabler:bookmarks",
				children: [
					<AppBuilderSavedStatesWidgetComponent
						key={2}
						namespace={namespace}
					/>,
				],
			});
		}

		if (
			!settings?.hideAttributeVisualization &&
			AppBuilderAttributeVisualizationWidgetComponent
		) {
			const hasSdtfData = outputs.some((output) => {
				const outputApi =
					sessionApi?.getOutputById(output.outputId) ||
					sessionApi?.getOutputByName(output.outputId)[0];
				// check if there is an output with sdtf format
				// this is used to determine if the Attributes tab should be shown
				return outputApi && outputApi.chunks;
			});

			if (hasSdtfData) {
				tabProps.tabs.push({
					value: "Attributes",
					tooltip: "Attributes",
					icon: "tabler:tags",
					children: [
						<AppBuilderAttributeVisualizationWidgetComponent
							key={0}
							visualizationMode={
								AttributeVisualizationVisibility.DefaultOn
							}
						/>,
					],
				});
			}
		}

		if (showDesktopClientPanel && !settings?.hideDesktopClients) {
			tabProps.tabs.push({
				value: "Stargate",
				tooltip: "Desktop Clients",
				icon: "tabler:network",
				children: [<DesktopClientPanel key={2} />],
			});
		}

		return tabProps;
	}, [settings, parameters, exports, outputs]);

	return <TabsComponent {...tabProps} />;
}
